import { StyleSheet, css } from 'aphrodite'
import { useCallback, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import type { Attachment } from '../types/project'

interface NoteAttachmentsProps {
  attachments: Attachment[]
  pendingFiles: File[] // For new records before save
  onPendingFilesChange: (files: File[]) => void
  onUpload?: (files: File[]) => Promise<void> // For existing records
  onDelete?: (id: number) => Promise<void>
  readOnly?: boolean
  maxFiles?: number // default 6
  maxTotalBytes?: number // default 250MB
}

// Helper to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`
}

// Helper to get file icon based on content type
const getFileIcon = (contentType: string): string => {
  if (contentType.startsWith('image/')) return 'fa-file-image'
  if (contentType === 'application/pdf') return 'fa-file-pdf'
  if (contentType.includes('word') || contentType.includes('document'))
    return 'fa-file-word'
  if (contentType.includes('excel') || contentType.includes('spreadsheet'))
    return 'fa-file-excel'
  if (contentType.includes('text')) return 'fa-file-lines'
  if (contentType.includes('zip') || contentType.includes('compressed'))
    return 'fa-file-zipper'
  return 'fa-file'
}

const NoteAttachments: React.FC<NoteAttachmentsProps> = ({
  attachments,
  pendingFiles,
  onPendingFilesChange,
  onUpload,
  onDelete,
  readOnly = false,
  maxFiles = 6,
  maxTotalBytes = 250 * 1024 * 1024, // 250MB
}) => {
  const [isUploading, setIsUploading] = useState(false)
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set())
  const [error, setError] = useState<string | null>(null)

  // Calculate current usage
  const { totalFiles, totalBytes, remainingFiles, remainingBytes } =
    useMemo(() => {
      const existingBytes = attachments.reduce((sum, a) => sum + a.file_size, 0)
      const pendingBytes = pendingFiles.reduce((sum, f) => sum + f.size, 0)
      const total = existingBytes + pendingBytes
      const files = attachments.length + pendingFiles.length

      return {
        totalFiles: files,
        totalBytes: total,
        remainingFiles: maxFiles - files,
        remainingBytes: maxTotalBytes - total,
      }
    }, [attachments, pendingFiles, maxFiles, maxTotalBytes])

  // Handle file drop
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setError(null)

      // Check file count limit
      if (totalFiles + acceptedFiles.length > maxFiles) {
        setError(
          `Cannot add ${acceptedFiles.length} file(s). Maximum ${maxFiles} files allowed.`,
        )
        return
      }

      // Check total size limit
      const newTotalBytes =
        totalBytes + acceptedFiles.reduce((sum, f) => sum + f.size, 0)
      if (newTotalBytes > maxTotalBytes) {
        setError(`Files exceed the ${formatFileSize(maxTotalBytes)} limit.`)
        return
      }

      // If onUpload is provided (existing record), upload immediately
      if (onUpload) {
        setIsUploading(true)
        try {
          await onUpload(acceptedFiles)
        } catch (err) {
          setError('Failed to upload files. Please try again.')
          console.error('Upload error:', err)
        } finally {
          setIsUploading(false)
        }
      } else {
        // Otherwise, add to pending files
        onPendingFilesChange([...pendingFiles, ...acceptedFiles])
      }
    },
    [
      totalFiles,
      totalBytes,
      maxFiles,
      maxTotalBytes,
      onUpload,
      pendingFiles,
      onPendingFilesChange,
    ],
  )

  // Configure dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: readOnly || isUploading || remainingFiles <= 0,
    multiple: true,
  })

  // Handle delete existing attachment
  const handleDelete = async (attachmentId: number) => {
    if (!onDelete) return

    setDeletingIds((prev) => new Set(prev).add(attachmentId))
    try {
      await onDelete(attachmentId)
    } catch (err) {
      setError('Failed to delete file. Please try again.')
      console.error('Delete error:', err)
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev)
        next.delete(attachmentId)
        return next
      })
    }
  }

  // Handle remove pending file
  const handleRemovePending = (index: number) => {
    const updated = [...pendingFiles]
    updated.splice(index, 1)
    onPendingFilesChange(updated)
  }

  const hasFiles = attachments.length > 0 || pendingFiles.length > 0
  const canAddMore = !readOnly && remainingFiles > 0 && remainingBytes > 0

  return (
    <div className={css(styles.container)}>
      {/* Usage counter */}
      <div className={css(styles.usageBar)}>
        <span className={css(styles.usageText)}>
          {totalFiles} of {maxFiles} files, {formatFileSize(totalBytes)} of{' '}
          {formatFileSize(maxTotalBytes)} used
        </span>
      </div>

      {/* Error message */}
      {error && <div className={css(styles.errorMessage)}>{error}</div>}

      {/* File list */}
      {hasFiles && (
        <div className={css(styles.fileList)}>
          {/* Existing attachments */}
          {attachments.map((attachment) => (
            <div key={attachment.id} className={css(styles.fileItem)}>
              <div className={css(styles.fileInfo)}>
                <i
                  className={`fa-regular ${getFileIcon(attachment.content_type_str)} ${css(styles.fileIcon)}`}
                />
                <a
                  href={attachment.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css(styles.fileName)}
                  title={attachment.original_filename}
                >
                  {attachment.original_filename}
                </a>
                <span className={css(styles.fileSize)}>
                  ({formatFileSize(attachment.file_size)})
                </span>
              </div>
              {!readOnly && onDelete && (
                <button
                  type="button"
                  onClick={() => handleDelete(attachment.id)}
                  disabled={deletingIds.has(attachment.id)}
                  className={css(styles.deleteButton)}
                  title="Delete file"
                >
                  {deletingIds.has(attachment.id) ? (
                    <i className="fa-solid fa-spinner fa-spin" />
                  ) : (
                    <i className="fa-solid fa-trash-can" />
                  )}
                </button>
              )}
            </div>
          ))}

          {/* Pending files (not yet uploaded) */}
          {pendingFiles.map((file, index) => (
            <div
              key={`pending-${file.name}-${file.size}-${file.lastModified}`}
              className={css(styles.fileItem)}
            >
              <div className={css(styles.fileInfo)}>
                <i
                  className={`fa-regular ${getFileIcon(file.type)} ${css(styles.fileIcon)}`}
                />
                <span className={css(styles.fileName)} title={file.name}>
                  {file.name}
                </span>
                <span className={css(styles.fileSize)}>
                  ({formatFileSize(file.size)})
                </span>
              </div>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => handleRemovePending(index)}
                  className={css(styles.deleteButton)}
                  title="Remove file"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Dropzone */}
      {canAddMore && (
        <div
          {...getRootProps()}
          className={css(
            styles.dropzone,
            isDragActive && styles.dropzoneActive,
            isUploading && styles.dropzoneDisabled,
          )}
        >
          <input {...getInputProps()} />
          {isUploading ? (
            <div className={css(styles.dropzoneContent)}>
              <i
                className="fa-solid fa-spinner fa-spin"
                style={{ fontSize: 24, color: '#6B7280' }}
              />
              <span className={css(styles.dropzoneText)}>Uploading...</span>
            </div>
          ) : (
            <div className={css(styles.dropzoneContent)}>
              <i
                className="fa-solid fa-cloud-arrow-up"
                style={{ fontSize: 24, color: '#9CA3AF' }}
              />
              <span className={css(styles.dropzoneText)}>
                {isDragActive
                  ? 'Drop files here...'
                  : 'Drag and drop files here, or click to select'}
              </span>
              <span className={css(styles.dropzoneHint)}>
                {remainingFiles} file(s) remaining,{' '}
                {formatFileSize(remainingBytes)} available
              </span>
            </div>
          )}
        </div>
      )}

      {/* Read-only empty state */}
      {readOnly && !hasFiles && (
        <div className={css(styles.emptyState)}>
          <i
            className="fa-regular fa-file"
            style={{ fontSize: 20, color: '#9CA3AF', marginBottom: 4 }}
          />
          <span className={css(styles.emptyText)}>No attachments</span>
        </div>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  usageBar: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  usageText: {
    fontSize: 12,
    color: '#6B7280',
  },
  errorMessage: {
    padding: '8px 12px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 6,
    color: '#DC2626',
    fontSize: 13,
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: 6,
    gap: 8,
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  fileIcon: {
    fontSize: 16,
    color: '#6B7280',
    flexShrink: 0,
  },
  fileName: {
    fontSize: 13,
    color: '#374151',
    textDecoration: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ':hover': {
      color: '#1C64F2',
      textDecoration: 'underline',
    },
  },
  fileSize: {
    fontSize: 12,
    color: '#9CA3AF',
    flexShrink: 0,
  },
  deleteButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    border: 'none',
    borderRadius: 4,
    backgroundColor: 'transparent',
    color: '#9CA3AF',
    cursor: 'pointer',
    transition: 'all 0.2s',
    flexShrink: 0,
    ':hover': {
      backgroundColor: '#FEE2E2',
      color: '#DC2626',
    },
    ':disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  },
  dropzone: {
    border: '2px dashed #D1D5DB',
    borderRadius: 8,
    padding: 24,
    cursor: 'pointer',
    transition: 'all 0.2s',
    ':hover': {
      borderColor: '#1C64F2',
      backgroundColor: 'rgba(28, 100, 242, 0.05)',
    },
  },
  dropzoneActive: {
    borderColor: '#1C64F2',
    backgroundColor: 'rgba(28, 100, 242, 0.1)',
    borderStyle: 'solid',
  },
  dropzoneDisabled: {
    cursor: 'not-allowed',
    opacity: 0.6,
    ':hover': {
      borderColor: '#D1D5DB',
      backgroundColor: 'transparent',
    },
  },
  dropzoneContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  dropzoneText: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
  },
  dropzoneHint: {
    fontSize: 12,
    color: '#9CA3AF',
    textAlign: 'center',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    backgroundColor: '#F9FAFB',
  },
  emptyText: {
    fontSize: 13,
    color: '#9CA3AF',
  },
})

export default NoteAttachments
